import { useEffect, useRef, useState } from 'react';

/**
 * Shows a loading state for a minimum duration so skeletons never flash
 * and disappear too quickly, preventing a blank-page feeling.
 *
 * - Returns `true` immediately when `isLoading` becomes true.
 * - Stays `true` for at least `minDuration` ms even if loading finishes early.
 * - Returns `false` only after BOTH conditions are met:
 *     1. `isLoading` is false  (data has arrived)
 *     2. `minDuration` ms have elapsed since loading started
 */
export function useDelayedLoading(isLoading: boolean, minDuration = 1000): boolean {
  const [show, setShow] = useState(isLoading);
  const startedAt = useRef<number | null>(isLoading ? Date.now() : null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isLoading) {
      startedAt.current = Date.now();
      setShow(true);
      return;
    }

    const elapsed = startedAt.current !== null ? Date.now() - startedAt.current : minDuration;
    const remaining = minDuration - elapsed;

    if (remaining <= 0) {
      setShow(false);
      startedAt.current = null;
    } else {
      timerRef.current = setTimeout(() => {
        setShow(false);
        startedAt.current = null;
      }, remaining);
    }

    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isLoading, minDuration]);

  return show;
}
